import React, { useState } from 'react';

import { Form, Formik } from 'formik';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import Input from '@components/common/Formik/FormikInputField';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Modal from '@components/common/Modals/Modal/Modal';
import Text from '@components/common/Text/Text';
import potService from '@services/domain/Lpc/PotService';
import { thankParticipantsSchema } from '@validations/potSchema';

import css from './ThankForm.module.scss';

type ThankFormProps = {
    isVisible: boolean;
    onHide: () => void;
    potRef: string;
    potTitle: string;
};

const ThankForm = ({ isVisible, onHide, potRef, potTitle }: ThankFormProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'common']);
    const { addToast } = useToasts();

    const [isLoading, setIsLoading] = useState(false);

    const getInitialValues = () => {
        return {
            message: t('amount.thanks.message-placeholder', { title: potTitle }),
        };
    };

    const onSubmit = (values: { message: string }) => {
        setIsLoading(true);
        potService
            .thankParticipants(potRef, values)
            .then((res) => {
                res.status === 200 && onHide();
                addToast(t(`success.MESSAGE_SENT`), {
                    appearance: 'success',
                    autoDismiss: true,
                });
            })
            .catch((err) => err)
            .finally(() => setIsLoading(false));
    };

    return (
        <Modal visible={isVisible} onHide={onHide} customClass={css.custom}>
            <div className={css.header}>
                <h5>{t('amount.thanks.thanks-title')}</h5>
                <Text variant={'body_02'} color={'txt-secondary'}>
                    {t('amount.thanks.thanks-desc')}
                </Text>
            </div>

            <Formik
                initialValues={getInitialValues()}
                validationSchema={thankParticipantsSchema}
                onSubmit={(values) => onSubmit(values)}
            >
                {() => (
                    <Form className={css.form}>
                        <div className={css.form__content}>
                            <div className={css.form__input}>
                                <Input
                                    customStyle={css.form__input__text}
                                    name="message"
                                    type="textarea"
                                    label={t('amount.thanks.message-label')}
                                    isFloatingLabel
                                />
                            </div>

                            {/* SUBMIT */}
                            <div className={css.form__submit}>
                                <Button
                                    type={'button'}
                                    customClass={css.form__submit__cta}
                                    variant={'secondary'}
                                    onClick={() => {
                                        onHide();
                                    }}
                                >
                                    {t('amount.thanks.cancel')}
                                </Button>
                                <Button
                                    type={'submit'}
                                    variant={'primary'}
                                    customClass={css.form__submit__cta}
                                    isLoading={isLoading}
                                    isDisabled={isLoading}
                                >
                                    {t('amount.thanks.confirm')}
                                </Button>
                                <input type="hidden" autoFocus={true} />
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default ThankForm;
